import {screenMd} from '@malt/joy-v2/src/js/utils/medias/media-queries';
import {allowUserScroll, preventUserScroll} from '../../services/preventUserScroll';
import {open, closed, clickOnPremobileButton} from '../../../events/header';
import {AWESOMEPLETE_OPEN, AWESOMEPLETE_CLOSE} from '../../../events/autocomplete';

const HEADER_WRAPPER_FIXED_CLASS = 'header-wrapper-fixed';
const SUB_NAVBAR_CLASS = {
    default: 'c-sub-header',
    invisible: 'c-sub-header_invisible',
};
const HEADER_SEARCH_CLASSES = {
    opened: 'opened',
    invisible: HEADER_WRAPPER_FIXED_CLASS + '_invisible',
    focus: 'focused',
    formType: {
        search: 'FORM_SEARCH',
        brief: 'FORM_BRIEF',
    },
};

const BODY_CLASSES = {
    noScroll: 'no-scroll',
};

const FORM_CLASS = {
    completed: 'completed',
    focus: 'focused',
    valid: 'valid',
};

Hop.defineModule('main-header-search-v2', (domRoot, options) => {
    /**
     * Hop Module to handle Top Header menu and search forms.
     * @param {Element} domRoot - root Element
     * @param {Object} options - Optional params to configure bejaviors on this module
     * @param {boolean} options.openedByDefault - if true, always display the subheader, and show search form by default. (only for desktop)
     * @return {function} destroy - Destroy all Listeners.
     */

    const subheader = domRoot.querySelector('.js-header-subheader');
    const subNavbar = document.querySelector(`.${SUB_NAVBAR_CLASS.default}`);
    const domAwesomPlete = subheader.querySelector('[data-awesomplete-input]');
    const domHeaderSearchBtn = domRoot.querySelectorAll('.js-search-btn');
    const domHeaderCloseSearch = domRoot.querySelector('[data-close-mobile-search]');
    const domMobileSearch = domRoot.hasAttribute('data-search-block-mobile');

    const openedByDefault = options.openedByDefault;

    // Elements
    const backdrop = document.createElement('div');
    let showBackdrop = false;

    const MOBILE_SEARCH_BREAKPOINT = 1200;
    const PRE_MOBILE_MODE_BREAKPOINT = 1440;

    let cacheScrollYPosition = 0;
    let scrollTicking = false;

    const debouncedOnResize = Hop.debounce(windowResizeListeners, 30);

    function handleHeaderOnScroll() {
        if (screenMd()) {
            if (!scrollTicking) {
                requestAnimationFrame(handleHeaderScrollClass);
            }
            scrollTicking = true;
        }
    }

    function handleHeaderScrollClass() {
        scrollTicking = false;
        const isScrollingDown = cacheScrollYPosition <= window.scrollY;
        const isScrollingBelowHeader = domRoot.offsetHeight <= window.scrollY;
        domRoot.classList.toggle(HEADER_SEARCH_CLASSES.invisible, isScrollingDown && isScrollingBelowHeader);
        cacheScrollYPosition = window.scrollY;

        // There is is additional subNavbar on some pages (ex: /open, /companies, /missions)
        if (subNavbar) {
            subNavbar.classList.toggle(SUB_NAVBAR_CLASS.invisible, isScrollingDown && isScrollingBelowHeader);
        }
    }

    function windowResizeListeners() {
        hideSearchOnPreMobileMode('resize');
        hideMobileSearchOnResize();
    }

    function mediaHigherThanBreakpoint(minOrMax = 'min') {
        return matchMedia(`(${minOrMax}-width:${MOBILE_SEARCH_BREAKPOINT}px)`).matches;
    }

    function isPreMobileMode() {
        return matchMedia(`(max-width: ${PRE_MOBILE_MODE_BREAKPOINT}px)`).matches && matchMedia(`(min-width: ${MOBILE_SEARCH_BREAKPOINT}px)`).matches;
    }

    function hasToBeOpenedByDefault() {
        return openedByDefault && mediaHigherThanBreakpoint();
    }

    function resetInvalidFields() {
        if (Hop && Hop.formFn && Hop.formFn.removeSingleMessage) {
            Hop.formFn.removeSingleMessage('q2');
        }
    }

    function setSearchInvisible() {
        resetInvalidFields();
        domRoot.classList.remove(HEADER_SEARCH_CLASSES.opened);
        destroyBackdrop();
    }

    function setSearchVisible(formType = 'search', withBackdrop = true) {
        domRoot.classList.add(HEADER_SEARCH_CLASSES.opened);
        domRoot.classList.toggle(HEADER_SEARCH_CLASSES.formType.search, formType === 'search');
        domRoot.classList.toggle(HEADER_SEARCH_CLASSES.formType.brief, formType === 'brief');
        if (withBackdrop && !showBackdrop) {
            initBackdrop();
        } else if (showBackdrop) {
            destroyBackdrop();
        }
    }

    function focusOnFirstInputByDefault() {
        domAwesomPlete.focus();
    }

    function jobValueCheck() {
        domAwesomPlete.addEventListener('keyup', inputHandler);
    }

    function inputHandler(event) {
        const form = event.target.closest('form');
        if (form) {
            checkFormValidity(form);
        }
    }

    function checkAllFormsValidity() {
        const forms = subheader.querySelectorAll('form');
        forms.forEach((form) => checkFormValidity(form));
    }

    function checkFormValidity(form) {
        if (form) {
            form.classList.toggle(FORM_CLASS.valid, domAwesomPlete.value.length >= 2);
            // Each Form has 2 submit buttons, one for desktop and the other for mobile.
            const allSubmitButton = form.querySelectorAll('button[type="submit"]');
            allSubmitButton.forEach((submitButton) => {
                submitButton.disabled = !(domAwesomPlete.value.length >= 2);
            });
            checkFormIsCompleted(form);
            removeFocusClassOnJobInput();
        }
    }

    function checkFormIsCompleted(form) {
        const inputs = form.querySelectorAll('input[type="text"]');
        const isComplete = Array.from(inputs).reduce((acc, input) => {
            if (!input.value) {
                acc = false;
            }
            return acc;
        }, true);
        form.classList.toggle(FORM_CLASS.completed, isComplete);
    }

    function removeFocusClassOnJobInput() {
        domAwesomPlete.classList.remove(FORM_CLASS.focus);
    }

    function setInputsAutompleteListener() {
        domRoot.addEventListener(AWESOMEPLETE_OPEN, addFocusClassOnJobInput);
        domAwesomPlete.addEventListener(AWESOMEPLETE_CLOSE, removeFocusClassOnJobInput);
    }

    function addFocusClassOnJobInput() {
        if (domAwesomPlete.nextSibling.childNodes.length) {
            domAwesomPlete.classList.add(FORM_CLASS.focus);
        }
    }

    function openSearch(event) {
        const formType = event.target.dataset.formType;
        const withBackdrop = !(hasToBeOpenedByDefault() && formType == 'search');

        setSearchInvisible();
        setSearchVisible(formType, withBackdrop);
        gtmPush();
        activeButton(event);

        Hop.events.trigger(open);

        if (!mediaHigherThanBreakpoint()) {
            preventUserScroll();
        }

        domRoot.classList.add(HEADER_SEARCH_CLASSES.opened, HEADER_SEARCH_CLASSES.focus);

        if (!hasToBeOpenedByDefault()) {
            focusOnFirstInputByDefault();
        }
    }

    function closeSearch(e) {
        if (e) {
            e.preventDefault();
        }

        setSearchInvisible();

        allowUserScroll();
        desactiveAllButtons();
        Hop.events.trigger(closed);
    }

    function activeButton(event) {
        desactiveAllButtons();
        event.target.classList.add('active');
    }

    function desactiveAllButtons() {
        domHeaderSearchBtn.forEach((button) => {
            button.classList.remove('active');
        });
    }

    function hideSearchOnPreMobileMode(eventType) {
        // We need to check how this function is triggered
        // because on mobile, userScroll.prevent() triggers a window resize event. And this event triggers this function and finally hide the search
        if (eventType && eventType === 'resize') {
            return;
        }

        if (isPreMobileMode() || mediaHigherThanBreakpoint('max')) {
            setSearchInvisible();
        }
    }

    function hideMobileSearchOnResize() {
        if (mediaHigherThanBreakpoint('min') && domMobileSearch) {
            domRoot.classList.remove(HEADER_SEARCH_CLASSES.opened, HEADER_SEARCH_CLASSES.focus);
        }
    }

    function setWindowResizeAndScrollListener() {
        window.addEventListener('resize', debouncedOnResize);
        window.addEventListener('scroll', handleHeaderOnScroll);
    }

    function listenToClickOnNewOpenSearchButton() {
        domHeaderSearchBtn.forEach((btn) => {
            btn.addEventListener('click', openSearch);
        });
    }

    function listenToPremobileMenuOpen() {
        Hop.events.on(clickOnPremobileButton, setSearchInvisible);
    }

    function listenToMobileMenu() {
        // TODO FIX ME
        // Hop.events.on(clickOnMobileButton, setSearchInvisible);
    }

    function listenToMobileCloseSearch() {
        if (domHeaderCloseSearch) {
            domHeaderCloseSearch.addEventListener('click', closeSearch);
        }
    }

    function listenToNewHeaderUserActions() {
        setWindowResizeAndScrollListener();
        listenToClickOnNewOpenSearchButton();
        listenToPremobileMenuOpen();
        hideSearchOnPreMobileMode();
        listenToMobileCloseSearch();
        listenToMobileMenu();

        // First check when dom is ready, for search page
        checkAllFormsValidity();
        jobValueCheck();

        if (mediaHigherThanBreakpoint('max')) {
            // If mobile
            setInputsAutompleteListener();
        }

        if (hasToBeOpenedByDefault()) {
            setSearchVisible('search', false);
        }
    }

    function gtmPush() {
        Hop.gtm.push({
            category: '_search',
            action: '_view_search',
            label: domHeaderSearchBtn[0].getAttribute('data-gtm-label'),
        });
    }

    function initBackdrop() {
        if (!showBackdrop) {
            document.body.classList.add(BODY_CLASSES.noScroll);
            backdrop.classList.add('header-subheader__backdrop');
            domRoot.appendChild(backdrop);
            backdrop.addEventListener('mousedown', closeSearch);
            showBackdrop = true;
        }
    }

    function destroyBackdrop() {
        if (showBackdrop) {
            document.body.classList.remove(BODY_CLASSES.noScroll);
            domRoot.removeChild(backdrop);
            backdrop.removeEventListener('mousedown', closeSearch);
            showBackdrop = false;
        }
    }

    listenToNewHeaderUserActions();

    return function destroy() {
        domHeaderSearchBtn.forEach((btn) => {
            btn.removeEventListener('click', openSearch);
        });

        if (domHeaderCloseSearch) {
            domHeaderCloseSearch.removeEventListener('click', closeSearch);
        }

        domAwesomPlete.removeEventListener('keyup', checkFormValidity);
        domAwesomPlete.removeEventListener(AWESOMEPLETE_OPEN, addFocusClassOnJobInput);
        domAwesomPlete.removeEventListener(AWESOMEPLETE_CLOSE, removeFocusClassOnJobInput);

        window.removeEventListener('resize', debouncedOnResize);

        if (showBackdrop) {
            backdrop.removeEventListener('mousedown', closeSearch);
        }
    };
});
